import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  Checkbox,
  Avatar,
  Chip,
  InputAdornment,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import { getChapterMembers, getSelfRoleEligibleMembers } from "../api/coordinatorApi";
import { Routes } from "../constant/api_url";
import { notification } from "../utils/messages";

const PAGE_SIZE = 20;

export const OBT_INCOMPLETE_MESSAGE = "This user's OBT is not completed, so you cannot use or assign them as a coordinator";

export const isAssignable = (member) => {
  const obtNotApproved = member.obt_approve === 0 || member.obt_approve === "0";
  const verified = member.m_verify_status === 4;
  return !(obtNotApproved && verified);
};

export default function MemberPickerModal({
  open,
  onClose,
  onConfirm,
  chapterId,
  excludedMemberIds = [],
  currentHolderId = null,
  eligiblePostTypeId = null,
  eligibleRoleId = null,
  emptyText = "No members found",
  loading: externalLoading = false,
  title = "Add Coordinators",
  subtitle = "Select members to assign roles in this system.",
  single = false,
  confirmLabel = "Add"
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const searchTimer = useRef(null);

  const fetchMembers = useCallback(async (pageNum, searchQuery) => {
    if (!chapterId) return;
    setLoading(true);
    try {
      // Self-role pickers use the eligibility endpoint scoped to the LT section
      // they were opened from; everything else uses the chapter member list.
      const eligibleMode = eligiblePostTypeId != null;
      const limit = eligibleMode ? 100 : PAGE_SIZE;
      const body = eligibleMode
        ? {
          chpt_id: parseInt(chapterId, 10),
          post_type_id: eligiblePostTypeId,
          ...(eligibleRoleId != null && { role_id: eligibleRoleId }),
          page: pageNum,
          limit
        }
        : {
          chpt_id: parseInt(chapterId, 10),
          page: pageNum,
          limit
        };
      if (searchQuery) body.search = searchQuery;

      const resp = eligibleMode
        ? await getSelfRoleEligibleMembers(body)
        : await getChapterMembers(body);
      if (resp?.status === 1) {
        const data = resp.output?.data || [];
        setMembers((prev) => (pageNum === 1 ? data : [...prev, ...data]));
        setHasMore(data.length === limit);
      } else if (eligibleMode) {
        notification({ type: "error", message: resp?.message || "Failed to load eligible members" })();
      }
    } catch (error) {
      console.error("Failed to load members:", error);
    } finally {
      setLoading(false);
    }
  }, [chapterId, eligiblePostTypeId, eligibleRoleId]);

  useEffect(() => {
    if (open && chapterId) {
      setMembers([]);
      setPage(1);
      setSelectedIds([]);
      setSearch("");
      fetchMembers(1, "");
    }
  }, [open, chapterId, fetchMembers]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setMembers([]);
      setPage(1);
      fetchMembers(1, val);
    }, 300);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMembers(nextPage, search);
  };

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (excludedMemberIds.includes(m.m_id)) return false;
      if (currentHolderId && m.m_id === currentHolderId) return false;
      return true;
    });
  }, [members, excludedMemberIds, currentHolderId]);

  const eligibleMode = eligiblePostTypeId != null;

  const handleToggle = (mId) => {
    if (single) {
      setSelectedIds((prev) => (prev.includes(mId) ? [] : [mId]));
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(mId) ? prev.filter((id) => id !== mId) : [...prev, mId]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedIds);
    onClose();
  };

  const getMemberPhoto = (photo) => {
    if (photo) return `${Routes.API_HOSTNAME}images/member/${photo}`;
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "85vh",
          maxWidth: 520,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        }
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(17, 24, 39, 0.5)", backdropFilter: "blur(4px)" }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: 3, pt: 3, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ "&:hover": { bgcolor: "grey.100" } }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search members by name or business..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
        />
      </Box>

      <Box sx={{ px: 3, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {eligibleMode ? `${filteredMembers.length} eligible member(s)` : `${filteredMembers.length} member(s)`}
        </Typography>
        <Chip
          label={`${selectedIds.length} selected`}
          size="small"
          sx={{ bgcolor: "secondary.lighter", color: "secondary.main", fontWeight: 600, fontSize: "0.7rem", height: 24 }}
        />
      </Box>

      <Divider />

      <DialogContent sx={{ px: 3, py: 1, maxHeight: 400 }}>
        {loading && members.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredMembers.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {filteredMembers.map((member) => {
              const isSelected = selectedIds.includes(member.m_id);
              const isExcludedByRole = excludedMemberIds.includes(member.m_id);
              const isHolder = currentHolderId && member.m_id === currentHolderId;
              const assignable = isAssignable(member);
              const disabled = isExcludedByRole || isHolder || !assignable;
              const blocked = !assignable;
              return (
                <Stack
                  key={member.m_id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: disabled ? "not-allowed" : "pointer",
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    bgcolor: disabled ? "grey.50" : isSelected ? "rgba(0, 88, 190, 0.04)" : "transparent",
                    opacity: disabled && !blocked ? 0.5 : 1,
                    "&:hover": disabled ? {} : {
                      bgcolor: isSelected ? "rgba(0, 88, 190, 0.08)" : "grey.50",
                      borderColor: isSelected ? "primary.main" : "grey.300"
                    },
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => {
                    if (blocked) {
                      notification({ type: "warn", message: OBT_INCOMPLETE_MESSAGE, position: "top" })();
                      return;
                    }
                    if (!disabled) handleToggle(member.m_id);
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    disabled={disabled}
                    sx={{ color: "grey.400", "&.Mui-checked": { color: "primary.main" } }}
                  />
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar
                      src={getMemberPhoto(member.photo)}
                      sx={{ width: 40, height: 40, border: "1px solid", borderColor: "grey.200" }}
                    >
                      {(member.m_name || "U").charAt(0).toUpperCase()}
                    </Avatar>
                    {(member.m_verify_status === 1 || member.m_verify_status === 2) && (
                      <Box
                        component="img"
                        src={member.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified.png"}
                        alt="Verified"
                        sx={{
                          width: 16,
                          height: 16,
                          position: "absolute",
                          top: -2,
                          right: -2
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: blocked ? "text.secondary" : "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {member.m_name || "Unknown"}
                      </Typography>
                      {blocked && (
                        <Chip
                          label="OBT pending"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            bgcolor: "warning.lighter",
                            color: "warning.dark",
                            "& .MuiChip-label": { px: 0.75 }
                          }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: blocked ? "text.secondary" : "text.secondary",
                        fontSize: "0.8rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block"
                      }}
                    >
                      {member.display_name || member.business_name || ""}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
            {hasMore && (
              <Button
                size="small"
                onClick={handleLoadMore}
                disabled={loading}
                sx={{ mt: 1, textTransform: "none" }}
              >
                {loading ? <CircularProgress size={16} /> : "Load more"}
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ color: "text.secondary", fontWeight: 600, px: 3, "&:hover": { bgcolor: "grey.100" } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedIds.length === 0 || externalLoading}
          startIcon={externalLoading ? <CircularProgress size={16} /> : null}
          sx={{ px: 3, fontWeight: 600, boxShadow: "sm", "&:hover": { boxShadow: "md" } }}
        >
          {confirmLabel}
          {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
